import { getQueryStringFromOptions, getResponse } from '@satellite/services/serviceUtilities';
import { cacheableGet } from '@satellite/services/httpClient';
import { getObjectAsRequestSearchString } from '@satellite/plugins/util.js';

/**
 * options: { fields: string[], joins: string[], includeMetaData: boolean }
 */

export default {
  async getDockById(id, params, options = {}) {
    const queryString = getQueryStringFromOptions(options);
    const response = await axios.get(`dock/${id}?${queryString}`, {
      params
    });
    return getResponse(response, options);
  },
  /**
   * Get dock by ID with improved API contract.
   *
   * @param {string} dockId - Dock ID
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching
   * @returns {Promise<unknown>} The response data
   */
  async getDockByIdV2(
    dockId,
    { getResponseConfig = {}, requestConfig = {}, enableCache = false } = {}
  ) {
    const url = `dock/${dockId}`;

    const axiosResponse = await cacheableGet(url, {
      ...requestConfig,
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },
  async getDocks(params, options = {}) {
    const queryString = getQueryStringFromOptions(options);
    const response = await axios.get(`dock?${queryString}`, {
      params
    });
    return getResponse(response, options);
  },
  async getListDocks(params, options = {}) {
    const response = await this.getDocks(params, {
      fields: ['name'],
      includeMetaData: options?.includeMetaData
    });
    return response;
  },
  async createCapacityDock(id, options) {
    const response = await axios.post(`dock/${id}/capacity`);
    return getResponse(response, options);
  },
  async unlinkCapacityDock(id, parentId, options) {
    const response = await axios.delete(`dock/${parentId}/capacity/${id}`);
    return getResponse(response, options);
  },
  async createDock(dock, options) {
    const response = await axios.post('dock', dock);
    return getResponse(response, options);
  },
  async saveDockOrder(docks, options) {
    const response = await axios.post(`dock/sort`, docks);
    return getResponse(response, options);
  },
  async updateDock(dock, options) {
    const response = await axios.patch(`dock/${dock.id}`, dock);
    return getResponse(response, options);
  },
  async deleteDock(id, options) {
    const response = await axios.delete(`dock/${id}`, { data: { hardDelete: true } });
    return getResponse(response, options);
  },
  async deactivateDock(id, options) {
    const response = await axios.patch(`dock/${id}`, { isActive: false });
    return getResponse(response, options);
  },
  async activateDock(id, options) {
    const response = await axios.patch(`dock/${id}`, { isActive: true });
    return getResponse(response, options);
  },
  /**
   * Days the given docks can still take a booking in, for the calendar's open-date hints.
   */
  async computeOpenDates({ start, end, dockIds }, options = {}, requestConfig = {}) {
    const response = await axios.post(
      'dock/compute-open-dates',
      { start, end, dockIds },
      requestConfig
    );
    return getResponse(response, options);
  },
  async getDocksByWarehouseId(warehouseId, options) {
    const response = await axios.get(
      `dock?${getObjectAsRequestSearchString({ warehouseId: { $eq: warehouseId } })}`
    );
    return getResponse(response, options);
  }
};
